import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppSettings } from 'pac-api';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { CartService } from '../cart.service';
import { CatchError, CPPA } from '../helpers';
import { LoggerService } from '../logger.service';
import { AsnInformationArray } from '../model/assoication';
import { FindPackageComboRequest } from '../model/find-package-combo.request';
import { MemberTypeModel } from '../model/member-type.model';
import { PackageModel } from '../model/package.model';
import { WizardModel } from '../model/wizard.model';
import { GenericErrorHandlerService } from '../shared/generic-error-handler.service';
import { WizardService } from '../wizard/wizard.service';

@Injectable({
  providedIn: 'root'
})
export class MembershipSelectionService {

  public Data: WizardModel;

  private memberTypes: MemberTypeModel[] = null;

  constructor(
    private http: HttpClient,
    private wizard: WizardService,
    private settings: AppSettings,
    private genericErrorHandler: GenericErrorHandlerService,
    private logger: LoggerService,
    private cart: CartService
  ) {
    let cache = sessionStorage.getItem('MembershipSelectionService');

    if (cache) {
      cache = JSON.parse(cache);
      if (cache) {
        this.Data = new WizardModel(this.cart, cache);

        if (this.Data.PackageCombo) {
          this.wizard.Package = new PackageModel(this.Data.PackageCombo);
        }
      }
    }

    if (!this.Data) {
      this.Data = new WizardModel(this.cart);
      this.Data.Associations[this.settings.pac_base_asn_code] = true;
    }

    try {
      this.logger.info(this.Data);
    } catch {
    }
  }

  public Cache() {
    if (this.Data.PackageCombo) {
      this.wizard.Package = this.Data.PackageCombo;
    }

    sessionStorage.setItem('MembershipSelectionService', JSON.stringify(this.Data));

    try {
      this.logger.info(this.Data);
    } catch {
    }
  }

  public Clear(): void {
    sessionStorage.removeItem('MembershipSelectionService');
  }

  public GetMemberTypes(filter: string = null): Observable<MemberTypeModel[]> {
    if (this.memberTypes && !filter) {
      return of(this.memberTypes);
    }

    const params = {};

    if (filter && filter == 'none') {
      params['filter'] = filter;
    }

    const observable = this.http.get(
      this.settings.pac_base_nf_url + 'api/MemberType',
      {
        params,
        withCredentials: true
      }
    ).pipe(catchError(CatchError), map((result: any) => {
      if (result.error) {
        return result;
      } else if (result instanceof Array) {
        return  result.map(record => new MemberTypeModel(record));
      }
    }), tap(result => {
      try {
        this.memberTypes = result;
        this.logger.info(result);
      } catch { }
    }, this.genericErrorHandler.Hanlder));

    return observable;
  }

  public GetPackageCombos(): Observable<PackageModel[]> {
    const request = new FindPackageComboRequest();

    AsnInformationArray.forEach(asn => {
      if (this.Data.Memberships[asn.code] && ! this.Data.Memberships[asn.code].Skip) {
        request[asn.code] = this.Data.Memberships[asn.code].Membership;
      } else {
        request[asn.code] = null;
      }
    });

    if (request.ABMP === null && request.ASCP === null && request.AHP === null && request.ANP === null) {
      return of(null);
    }

    return this.http.post(
      this.settings.pac_base_nf_url + 'api/MemberPackageCombo/FindPackageCombos',
      request,
      {
        withCredentials: true
      }
    ).pipe(
      catchError(CatchError),
      map((result: any) => {
        if (result.error) {
          return result;
        } else if (result instanceof Array) {
          return result.map(p02 => new PackageModel(p02));
        }
      }),
      tap(result => {
        try {
          this.logger.info(result);
        } catch { }
      }, this.genericErrorHandler.Hanlder)
    );
  }

  private GetPackageComboyByKey(key: string): Observable<PackageModel> {
    return this.http.get(
      this.settings.pac_base_nf_url + 'api/MemberPackageCombo/Get/' + key,
      {
        withCredentials: true
      }
    ).pipe(
      catchError(CatchError),
      map((result: any) => {
        if (result.error) {
          return result;
        } else if ('p02_key' in result) {
          return new PackageModel(result);
        }
      }),
      tap(result => {
        try {
          this.logger.info(result);
        } catch { }
      }, this.genericErrorHandler.Hanlder)
    );
  }

  public GetPackageCombo(key: string = null): Observable<PackageModel> {
    if (typeof key === 'string' && key !== '') {
      return this.GetPackageComboyByKey(key);
    }

    const request = new FindPackageComboRequest();

    AsnInformationArray.forEach(asn => {
      if (this.Data.Memberships[asn.code] && ! this.Data.Memberships[asn.code].Skip) {
        request[asn.code] = this.Data.Memberships[asn.code].Membership;
      } else {
        request[asn.code] = null;
      }
    });

    if (request.ABMP === null && request.ASCP === null && request.AHP === null && request.ANP === null) {
      return of(new PackageModel());
    }

    const observable = this.http.post(
      this.settings.pac_base_nf_url + 'api/MemberPackageCombo/FindPackageCombo',
      request,
      {
        withCredentials: true
      }
    ).pipe(catchError(CatchError), map((result: any) => {
      if (result.error) {
        return result;
      } else if ('p02_key' in result) {
        return new PackageModel(result);
      }
    }), tap(result => {
      try {
        this.logger.info(result);
      } catch { }
    }, this.genericErrorHandler.Hanlder));

    return observable;
  }

  public GetMonthlyInstallmentsPrice(data?: any): number {
    if (! data) {
      data = this.Data.Memberships;
    }

    if (data.ABMP && data.ABMP.Level === 'Professional') {

      // Levels
      const dualie = (data.ABMP && data.ABMP.Level === 'Professional') && (data.ASCP && data.ASCP.Level === 'Professional');

      // Discount codes
      const ec = this.cart.Data.ExistingSubscriptions.some(ivd => ivd.prd_code.startsWith('AExamCoach'));
      const a5mm = this.cart.Data.ExistingSubscriptions.some(ivd => ivd.prc_code.startsWith('A5mm'));
      const studentABMP = this.cart.Data.ExistingMemberships.some(mbr => mbr.mbr_asn_code === 'ABMP' && mbr.mbt_code === 'Student')
        && ! this.cart.Data.ExistingMemberships.some(CPPA);
      const studentASCP = this.cart.Data.ExistingMemberships.some(mbr => mbr.mbr_asn_code === 'ASCP' && mbr.mbt_code === 'Student')
        && ! this.cart.Data.ExistingMemberships.some(CPPA);

      let price: number = dualie ? 259 : 229;

      if (studentABMP) {
        price -= studentASCP ? 59 : 49;
      }

      if (studentABMP || ec || a5mm) {
        price += 9;

        if (ec && a5mm) {
          price = 0;
        } else {
          if (ec) {
            price -= studentABMP ? 105 : 154;
          }

          if (a5mm) {
            price -= studentABMP ? 105 : 154;
          }
        }
      } else {
        price += dualie ? 17 : 11;
      }

      //Force ASCP upgrade to be $13
      if(dualie && (studentABMP || studentASCP)) {
        price = 156;
      }

      const monthly = price / 12;

      return Math.floor(monthly * 100) / 100;
    }

    return null;
  }

  public BPPIneligible(): boolean | string {
    if(this.wizard.getIndividual().BPPIneligible === false || this.wizard.getIndividual().BPPIneligible == 'false') return false;
    return this.wizard.getIndividual().BPPIneligible;
  }
}
